import { apiClient } from '../../api/index.js';
import type {
    Account,
    ITradingProvider,
    Position,
    Order,
} from '@ocktra/shared-types';

import { ApiResponse, Funds, PlaceOrderInput, type KiteCredentials, type ProviderConnection } from '@ocktra/shared-types';
import { connectToKite } from './utils.js';


export interface ZerodhaCredentials {
    apiKey: string;
    apiSecret: string;
    accessToken: string;
}

export class KiteTradingProvider implements ITradingProvider {
    private credentials: {
        apiKey: string;
        apiSecret: string;
    } = {
            apiKey: '',
            apiSecret: '',
        };
    private connected = false;
    private connection: ProviderConnection | null = null;

    // private url="https://kite.zerodha.com/connect/login?v=3" // TODO: Use for authentication

    public updateCredentials(credentials: Record<string, string>): void {
        if (!credentials.apiKey || !credentials.apiSecret) {
            throw new Error('API Key and API Secret are required for Zerodha Kite connection');
        }
        this.credentials.apiKey = credentials.apiKey;
        this.credentials.apiSecret = credentials.apiSecret;
    }

    updateConnectionDetails(connection: ProviderConnection): void {
        this.connection = connection;
        this.connected = connection.status === 'connected';
    }


    async connect(credentials: Record<string, string>): Promise<ProviderConnection> {
        // Update credentials from parameter
        if (!credentials.apiKey || !credentials.apiSecret) {
            throw new Error('API Key and API Secret are required for Zerodha Kite connection');
        }
        this.credentials.apiKey = credentials.apiKey;
        this.credentials.apiSecret = credentials.apiSecret;

        const kiteCredentials: KiteCredentials = {
            apiKey: this.credentials.apiKey,
            apiSecret: this.credentials.apiSecret,
        }


        try {
            const providerType = 'trading_provider';
            const response = await connectToKite(kiteCredentials, providerType);

            this.credentials = kiteCredentials;
            this.connection = response

            await this.initiateOAuthFlow();
            return response;
        } catch (err) {
            console.error('[ZerodhaTradingProvider] Connection error:', err);
            throw new Error(`Failed to connect to Zerodha Kite: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }


    }

    private initiateOAuthFlow(): Promise<void> {
        return new Promise((resolve, reject) => {
            const apiKey = this.credentials.apiKey;
            console.log(resolve)


            // Construct Kite Connect login URL
            const loginUrl = `https://kite.zerodha.com/connect/login?api_key=${apiKey}&v=3`;

            // Open popup window
            const width = 600;
            const height = 700;
            const left = window.screenX + (window.outerWidth - width) / 2;
            const top = window.screenY + (window.outerHeight - height) / 2;

            const popup = window.open(
                loginUrl,
                'Zerodha Login',
                `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`
            );

            if (!popup) {
                reject(new Error('Popup blocked. Please allow popups for this site.'));
                return;
            }

            // Poll for redirect with request_token
            const pollTimer = setInterval(() => {
                try {
                    // Check if popup is closed
                    if (popup.closed) {
                        clearInterval(pollTimer);
                        reject(new Error('OAuth flow cancelled by user'));
                        return;
                    }

                } catch (e) {
                    // Cross-origin error is expected while on Zerodha domain
                    // Continue polling
                }
            }, 500);

            // Timeout after 5 minutes
            setTimeout(() => {
                clearInterval(pollTimer);
                if (!popup.closed) {
                    popup.close();
                }
                reject(new Error('OAuth flow timed out'));
            }, 5 * 60 * 1000);
        });
    }


    async disconnect(): Promise<void> {
        console.log('[Trading] Disconnecting from Zerodha Kite trading provider...');
        this.connected = false;
    }

    isConnected(): boolean {
        return this.connected;
    }

    async getAccount(): Promise<Account> {
        this.ensureConnected();
        // Implement actual API call to get account details
        throw new Error('Method not implemented.');
    }

    async placeOrder(_request: PlaceOrderInput): Promise<Order> {
        this.ensureConnected();

        const params = {
            exchange: "NSE",
            tradingsymbol: _request.tradingSymbol,
            transaction_type: _request.quantity > 0 ? "BUY" : "SELL",
            order_type: _request.orderType,
            quantity: Math.abs(_request.quantity),
            product: _request.product,
            validity: "DAY",
            price: _request.price || 0,
            trigger_price: _request.triggerPrice || 0,
            variety: "regular"
        }

        try {
            // Implement actual API call to place order
            const response = await apiClient.post<ApiResponse<Order>>('/broker/order/place', {
                connId: this.connection?.id,
                order: params
            });

            console.log('[ZerodhaTradingProvider] Order placement response:', response);

            if (!response.data) {
                throw new Error('No order data returned from API');
            }

            return response.data;
        } catch (err) {
            console.error('[ZerodhaTradingProvider] Error placing order:', err);
            throw new Error(`Failed to place order: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
    }

    async cancelOrder(_orderId: string): Promise<void> {
        this.ensureConnected();
        // Implement actual API call to cancel order
        try {
            const response = await apiClient.post<ApiResponse<null>>('/broker/order/cancel', {
                connId: this.connection?.id,
                orderId: _orderId
            });
            console.log('[ZerodhaTradingProvider] Order cancellation response:', response);
        } catch (err) {
            console.error('[ZerodhaTradingProvider] Error cancelling order:', err);
            throw new Error(`Failed to cancel order: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
    }

    async getOrder(_orderId: string): Promise<Order> {
        this.ensureConnected();
        // Implement actual API call to get order details
        try {
            const response = await apiClient.post<ApiResponse<Order>>('/broker/order/get', {
                connId: this.connection?.id,
                orderId: _orderId
            });
            console.log('[ZerodhaTradingProvider] Get order response:', response);

            if (!response.data) {
                throw new Error('No order data returned from API');
            }

            return response.data;
        } catch (err) {
            console.error('[ZerodhaTradingProvider] Error fetching order:', err);
            throw new Error(`Failed to fetch order: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
    }

    async getOrders(_symbol?: string): Promise<Order[]> {
        this.ensureConnected();
        // Implement actual API call to get list of orders
        try {
            const response = await apiClient.post<ApiResponse<{ orders: Order[], count: number }>>('/broker/orders', {
                connId: this.connection?.id
            });

            console.log('[ZerodhaTradingProvider] Get orders response:', response);

            if (!response.data) {
                throw new Error('No orders data returned from API');
            }

            return response.data.orders;
        } catch (err) {
            console.error('[ZerodhaTradingProvider] Error fetching orders:', err);
            throw new Error(`Failed to fetch orders: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
    }

    async getPositions(): Promise<Position[]> {
        this.ensureConnected();
        // Implement actual API call to get list of positions
        try {
            const response = await apiClient.post<ApiResponse<{ net: Position[], day: Position[] }>>('/broker/positions', {
                connId: this.connection?.id
            });

            console.log('[ZerodhaTradingProvider] Get positions response:', response);

            if (!response.data) {
                throw new Error('No positions data returned from API');
            }

            return response.data.net;
        } catch (err) {
            console.error('[ZerodhaTradingProvider] Error fetching positions:', err);
            throw new Error(`Failed to fetch positions: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
    }

    async getFunds(): Promise<Funds> {
        this.ensureConnected();
        // For now, return mock data
        // TODO: Implement actual API call to get margin data


        // @ocktra/api:dev:   equity: {
        // @ocktra/api:dev:     enabled: true,
        // @ocktra/api:dev:     net: 50000,
        // @ocktra/api:dev:     available: {
        // @ocktra/api:dev:       adhoc_margin: 0,
        // @ocktra/api:dev:       cash: 0,
        // @ocktra/api:dev:       opening_balance: 0,
        // @ocktra/api:dev:       live_balance: 50000,
        // @ocktra/api:dev:       collateral: 0,
        // @ocktra/api:dev:       intraday_payin: 50000
        // @ocktra/api:dev:     },
        // @ocktra/api:dev:     utilised: {
        // @ocktra/api:dev:       debits: 0,
        // @ocktra/api:dev:       exposure: 0,
        // @ocktra/api:dev:       m2m_realised: 0,
        // @ocktra/api:dev:       m2m_unrealised: 0,
        // @ocktra/api:dev:       option_premium: 0,
        // @ocktra/api:dev:       payout: -0,
        // @ocktra/api:dev:       span: 0,
        // @ocktra/api:dev:       holding_sales: 0,
        // @ocktra/api:dev:       turnover: 0,
        // @ocktra/api:dev:       liquid_collateral: 0,
        // @ocktra/api:dev:       stock_collateral: 0,
        // @ocktra/api:dev:       equity: 0,
        // @ocktra/api:dev:       delivery: 0
        // @ocktra/api:dev:     }
        // @ocktra/api:dev:   },
        // @ocktra/api:dev:   commodity: {
        // @ocktra/api:dev:     enabled: true,
        // @ocktra/api:dev:     net: 0,
        // @ocktra/api:dev:     available: {
        // @ocktra/api:dev:       adhoc_margin: 0,
        // @ocktra/api:dev:       cash: 0,
        // @ocktra/api:dev:       opening_balance: 0,
        // @ocktra/api:dev:       live_balance: 0,
        // @ocktra/api:dev:       collateral: 0,
        // @ocktra/api:dev:       intraday_payin: 0
        // @ocktra/api:dev:     },
        // @ocktra/api:dev:     utilised: {
        // @ocktra/api:dev:       debits: 0,
        // @ocktra/api:dev:       exposure: 0,
        // @ocktra/api:dev:       m2m_realised: 0,
        // @ocktra/api:dev:       m2m_unrealised: 0,
        // @ocktra/api:dev:       option_premium: 0,
        // @ocktra/api:dev:       payout: -0,
        // @ocktra/api:dev:       span: 0,
        // @ocktra/api:dev:       holding_sales: 0,
        // @ocktra/api:dev:       turnover: 0,
        // @ocktra/api:dev:       liquid_collateral: 0,
        // @ocktra/api:dev:       stock_collateral: 0,
        // @ocktra/api:dev:       equity: 0,
        // @ocktra/api:dev:       delivery: 0
        // @ocktra/api:dev:     }
        // @ocktra/api:dev:   }
        // @ocktra/api:dev: }

        const response = await apiClient.post<ApiResponse<Funds>>('/broker/funds', {
            connId: this.connection?.id
        });

        console.log('[ZerodhaTradingProvider] Get funds response:', response);

        if (!response.data) {
            throw new Error('No funds data returned from API');
        }

        return response.data;
    }

    private ensureConnected() {
        if (!this.connected) {
            throw new Error('Not connected to Zerodha Kite trading provider.');
        }
    }
}
